/**
 * Utilidad para imprimir comanda, precuenta o factura térmica usando los drivers nativos de Windows / Sistema.
 * Abre la ventana estándar de impresión de Windows con un diseño limpio, profesional y legible.
 */
import QRCode from "qrcode";
import type { DatosDianTicketCliente } from "@tentacion/shared";
import { formatearMoneda } from "./formato";

export interface DatosTicketWindows {
  dian?: DatosDianTicketCliente | null;
  titulo: string;
  subtitulo?: string;
  mesaNumero?: number | string;
  meseraNombre?: string;
  fecha?: Date | string;
  items: Array<{
    cantidad: number;
    nombre: string;
    precio?: number | string;
    subtotal?: number | string;
    notas?: string | null;
  }>;
  subtotal?: number;
  descuento?: number;
  propina?: number;
  total?: number;
  pagos?: Array<{ medioPago: string; monto: number }>;
  clienteNombre?: string | null;
  clienteDocumento?: string | null;
  notasGenerales?: string | null;
  logoUrl?: string | null;
  imprimirLogoTicket?: boolean;
  encabezadoTicket?: string | null;
  pieTicket?: string | null;
  sucursalNombre?: string | null;
}

/**
 * Escapa texto antes de meterlo en el HTML del ticket para evitar inyecciones
 * o problemas de renderizado en navegadores y motores de impresión.
 */
function escaparHtml(texto: string): string {
  return texto
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** QR como SVG en línea: se genera al instante, sin esperar a que cargue una imagen antes de imprimir. */
function qrSvg(texto: string): string {
  const qr = QRCode.create(texto, { errorCorrectionLevel: "M" });
  const n = qr.modules.size;
  let path = "";
  for (let fila = 0; fila < n; fila++) {
    for (let col = 0; col < n; col++) {
      if (qr.modules.get(fila, col)) path += `M${col} ${fila}h1v1h-1z`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-2 -2 ${n + 4} ${n + 4}" width="140" height="140" shape-rendering="crispEdges"><rect x="-2" y="-2" width="${n + 4}" height="${n + 4}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`;
}

function dianEncabezadoHtml(dian?: DatosDianTicketCliente | null): string {
  if (!dian) return "";
  const rango =
    dian.resolucionPrefijo !== undefined && dian.rangoDesde && dian.rangoHasta
      ? `<div>Del ${escaparHtml(`${dian.resolucionPrefijo ?? ""}${dian.rangoDesde}`)} al ${escaparHtml(`${dian.resolucionPrefijo ?? ""}${dian.rangoHasta}`)}</div>`
      : "";
  return `
    <div style="font-size: 10px; line-height: 1.35; margin-top: 4px;">
      ${dian.emisorRazonSocial ? `<div class="bold">${escaparHtml(dian.emisorRazonSocial)}</div>` : ""}
      ${dian.emisorNit ? `<div>NIT ${escaparHtml(dian.emisorNit)}</div>` : ""}
      ${dian.responsableIva === false ? "<div>No responsable de IVA</div>" : ""}
      <div class="bold" style="font-size: 12px; margin-top: 4px;">No. ${escaparHtml(dian.numero)}</div>
      ${dian.resolucionNumero ? `<div>Resolución DIAN ${escaparHtml(dian.resolucionNumero)}</div>` : ""}
      ${rango}
      ${dian.resolucionVigencia ? `<div>Vigencia: ${escaparHtml(dian.resolucionVigencia)}</div>` : ""}
    </div>`;
}

function dianPieHtml(dian?: DatosDianTicketCliente | null): string {
  if (!dian) return "";
  return `
    <div class="text-center" style="font-size: 9px; line-height: 1.35;">
      <div class="bold">${dian.tipoDocumento === "POS" ? "CUDE" : "CUFE"}:</div>
      <div style="word-break: break-all;">${escaparHtml(dian.cufe)}</div>
      ${dian.qrUrl ? `<div style="margin: 6px auto;">${qrSvg(dian.qrUrl)}</div>` : ""}
      <div>Representación gráfica del ${dian.tipoDocumento === "POS" ? "documento equivalente electrónico" : "documento electrónico"}</div>
      ${dian.ambientePruebas ? '<div class="bold">*** AMBIENTE DE PRUEBAS ***</div>' : ""}
      ${dian.softwareNombre ? `<div>Software: ${escaparHtml(dian.softwareNombre)}</div>` : ""}
    </div>
    <div class="divider"></div>`;
}

export function imprimirTicketWindows(datos: DatosTicketWindows) {
  const fechaObj = typeof datos.fecha === "string" ? new Date(datos.fecha) : datos.fecha || new Date();
  const fechaFormateada = fechaObj.toLocaleString("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Bogota",
  });

  const sucursalNom = datos.sucursalNombre || "TENTACIÓN FRUTAL";

  const encabezadoLineas = datos.encabezadoTicket
    ? datos.encabezadoTicket
        .split("\n")
        .map((l) => `<div class="encabezado-linea">${escaparHtml(l.trim())}</div>`)
        .join("")
    : `<div class="empresa-titulo">${escaparHtml(sucursalNom.toUpperCase())}</div>`;

  const pieLineas = datos.pieTicket
    ? datos.pieTicket
        .split("\n")
        .map((l) => `<div>${escaparHtml(l.trim())}</div>`)
        .join("")
    : `<div>¡Gracias por preferirnos!</div><div class="web-url">tentacionfrutal.com</div>`;

  const esCierre =
    datos.titulo.toLowerCase().includes("cierre") ||
    datos.titulo.toLowerCase().includes("arqueo");

  const totalLabel = esCierre ? "TOTAL VENTAS DEL TURNO:" : "TOTAL A PAGAR:";

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Ticket - ${escaparHtml(sucursalNom)}</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0mm;
          }
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Courier New";
            width: 76mm;
            margin: 0 auto;
            padding: 4mm 2mm;
            color: #000;
            background: #fff;
            font-size: 12px;
            line-height: 1.35;
            -webkit-font-smoothing: antialiased;
          }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .bold { font-weight: 700; }
          .black { font-weight: 900; }

          .logo-box {
            text-align: center;
            margin-bottom: 6px;
          }
          .logo-img {
            max-height: 65px;
            max-width: 200px;
            object-fit: contain;
            filter: grayscale(100%) contrast(180%);
          }

          .empresa-titulo {
            font-size: 16px;
            font-weight: 900;
            letter-spacing: -0.2px;
            text-transform: uppercase;
            margin-bottom: 2px;
          }
          .encabezado-linea {
            font-size: 11px;
            font-weight: 700;
          }

          .badge-titulo {
            display: inline-block;
            font-size: 12px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 3px 8px;
            margin: 6px 0 3px 0;
            border: 1.5px solid #000;
            border-radius: 4px;
            background: #f4f4f4;
          }

          .subtitle {
            font-size: 11px;
            font-weight: 600;
            color: #333;
          }

          .divider {
            border-top: 1px dashed #000;
            margin: 6px 0;
          }
          .divider-solid {
            border-top: 1.5px solid #000;
            margin: 6px 0;
          }

          .mesa-box {
            font-size: 18px;
            font-weight: 900;
            border: 2px solid #000;
            border-radius: 6px;
            padding: 4px;
            margin: 6px 0;
            text-align: center;
            background: #f8f8f8;
            letter-spacing: 0.5px;
          }

          .info-block {
            font-size: 11px;
            line-height: 1.4;
            margin: 4px 0;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
          }

          .tabla-header {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            font-weight: 900;
            text-transform: uppercase;
            border-bottom: 1.5px solid #000;
            padding-bottom: 2px;
            margin-bottom: 4px;
          }

          .item-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 3px;
          }
          .item-cant {
            font-weight: 900;
            min-width: 22px;
          }
          .item-nombre {
            flex: 1;
            font-weight: 700;
            padding-right: 4px;
            word-break: break-word;
          }
          .item-precio {
            font-weight: 800;
            text-align: right;
            white-space: nowrap;
          }

          .item-nota {
            font-size: 10px;
            font-style: italic;
            margin-left: 22px;
            margin-bottom: 3px;
            color: #333;
          }

          .resumen-linea {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin-bottom: 2px;
          }

          .total-box {
            border: 2px solid #000;
            border-radius: 6px;
            padding: 6px 8px;
            margin-top: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 16px;
            font-weight: 900;
            background: #f4f4f4;
          }

          .pagos-block {
            font-size: 11px;
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px dashed #000;
          }

          .pie-box {
            text-align: center;
            margin-top: 10px;
            font-size: 11px;
            line-height: 1.4;
          }
          .web-url {
            font-weight: 800;
            margin-top: 2px;
          }
        </style>
      </head>
      <body>
        <div class="text-center">
          ${
            datos.imprimirLogoTicket !== false && datos.logoUrl
              ? `<div class="logo-box"><img src="${datos.logoUrl}" class="logo-img" alt="Logo" /></div>`
              : ""
          }
          ${encabezadoLineas}
          <div>
            <span class="badge-titulo">${escaparHtml(datos.titulo.toUpperCase())}</span>
          </div>
          ${datos.subtitulo ? `<div class="subtitle">${escaparHtml(datos.subtitulo)}</div>` : ""}
          ${dianEncabezadoHtml(datos.dian)}
        </div>

        <div class="divider"></div>

        ${datos.mesaNumero ? `<div class="mesa-box">MESA #${datos.mesaNumero}</div>` : ""}

        <div class="info-block">
          <div class="info-row">
            <span>Fecha / Hora:</span>
            <span class="bold">${fechaFormateada}</span>
          </div>
          ${
            datos.meseraNombre
              ? `<div class="info-row">
                  <span>Atendido por:</span>
                  <span class="bold">${escaparHtml(datos.meseraNombre)}</span>
                </div>`
              : ""
          }
          ${
            datos.clienteNombre
              ? `<div class="info-row">
                  <span>Cliente:</span>
                  <span class="bold">${escaparHtml(datos.clienteNombre)}</span>
                </div>`
              : ""
          }
          ${
            datos.clienteDocumento
              ? `<div class="info-row">
                  <span>NIT / Cédula:</span>
                  <span class="bold">${escaparHtml(datos.clienteDocumento)}</span>
                </div>`
              : ""
          }
        </div>

        <div class="divider-solid"></div>

        <div class="tabla-header">
          <span>${esCierre ? "Concepto de Arqueo" : "Cant · Descripción"}</span>
          <span>${esCierre ? "Monto" : "Importe"}</span>
        </div>

        ${datos.items
          .map((item) => {
            const valPrecio =
              item.subtotal !== undefined
                ? Number(item.subtotal)
                : item.precio !== undefined
                ? Number(item.precio) * item.cantidad
                : undefined;

            const precioTexto =
              valPrecio !== undefined
                ? valPrecio < 0
                  ? `-${formatearMoneda(Math.abs(valPrecio))}`
                  : formatearMoneda(valPrecio)
                : "";

            return `
              <div class="item-row">
                ${!esCierre ? `<span class="item-cant">${item.cantidad}x</span>` : ""}
                <span class="item-nombre">${escaparHtml(item.nombre)}</span>
                ${valPrecio !== undefined ? `<span class="item-precio">${precioTexto}</span>` : ""}
              </div>
              ${item.notas ? `<div class="item-nota">↳ ${escaparHtml(item.notas)}</div>` : ""}
            `;
          })
          .join("")}

        <div class="divider-solid"></div>

        ${
          datos.subtotal !== undefined && datos.subtotal !== datos.total
            ? `
          <div class="resumen-linea">
            <span>Subtotal:</span>
            <span class="bold">${formatearMoneda(datos.subtotal)}</span>
          </div>
        `
            : ""
        }

        ${
          datos.descuento && datos.descuento > 0
            ? `
          <div class="resumen-linea" style="color: #000;">
            <span>Descuento:</span>
            <span class="bold">-${formatearMoneda(datos.descuento)}</span>
          </div>
        `
            : ""
        }

        ${
          datos.propina && datos.propina > 0
            ? `
          <div class="resumen-linea">
            <span>Propina (Voluntaria):</span>
            <span class="bold">+${formatearMoneda(datos.propina)}</span>
          </div>
        `
            : ""
        }

        ${
          datos.total !== undefined
            ? `
          <div class="total-box">
            <span>${totalLabel}</span>
            <span>${formatearMoneda(datos.total)}</span>
          </div>
        `
            : ""
        }

        ${
          datos.pagos && datos.pagos.length > 0
            ? `
          <div class="pagos-block">
            <div class="bold" style="margin-bottom: 2px; font-size: 10px; text-transform: uppercase;">Medios de Pago:</div>
            ${datos.pagos
              .map(
                (p) => `
              <div class="info-row">
                <span>· ${escaparHtml(p.medioPago)}:</span>
                <span class="bold">${formatearMoneda(p.monto)}</span>
              </div>
            `,
              )
              .join("")}
          </div>
        `
            : ""
        }

        ${
          datos.notasGenerales
            ? `
          <div class="divider"></div>
          <div style="font-size: 10px; line-height: 1.3;">
            <strong style="text-transform: uppercase;">Observación / Justificación:</strong> ${escaparHtml(datos.notasGenerales)}
          </div>
        `
            : ""
        }

        <div class="divider"></div>

        ${
          esCierre
            ? `
          <div style="font-size: 10px; margin-top: 15px; text-align: center;">
            <div>Firma del Responsable de Caja:</div>
            <div style="margin-top: 30px; border-bottom: 1px solid #000; width: 70%; margin-left: auto; margin-right: auto;"></div>
            <div style="margin-top: 4px; font-weight: 700; text-transform: uppercase;">${escaparHtml(datos.subtitulo || "Cajero")}</div>
          </div>
        `
            : `
          <div style="font-size: 11px; margin-top: 4px; display: flex; justify-content: space-between;">
            <span>N° de Productos: <strong class="black">${datos.items.reduce((s, i) => s + i.cantidad, 0)}</strong></span>
            ${datos.meseraNombre ? `<span>Atendió: <strong>${escaparHtml(datos.meseraNombre)}</strong></span>` : ""}
          </div>
        `
        }

        <div class="divider"></div>

        ${dianPieHtml(datos.dian)}

        <div class="pie-box">
          ${pieLineas}
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
    </html>
  `;

  const ventana = window.open("", "_blank", "width=420,height=650");
  if (ventana) {
    ventana.document.write(html);
    ventana.document.close();
  }
}

/**
 * Imprime el letrero de QR de una mesa (para pegar en la mesa física) usando
 * la misma ventana de impresión nativa que el resto de tickets.
 */
export function imprimirQrMesa(datos: {
  numero: number;
  nombre?: string | null;
  dataUrlQr: string;
  url: string;
}) {
  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>QR Mesa ${datos.numero} - Tentación Frutal</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 0mm;
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
            width: 76mm;
            margin: 0 auto;
            padding: 4mm;
            color: #000;
            background: #fff;
            text-align: center;
          }
          .title { font-size: 16px; font-weight: 900; margin-bottom: 2px; text-transform: uppercase; }
          .mesa-box {
            font-size: 20px;
            font-weight: 900;
            border: 2px solid #000;
            border-radius: 6px;
            padding: 6px;
            margin: 8px 0;
            background: #f8f8f8;
          }
          img { width: 55mm; height: 55mm; margin: 6px 0; }
          .instrucciones { font-size: 12px; font-weight: 700; margin-top: 6px; }
          .url { font-size: 9px; word-break: break-all; margin-top: 6px; color: #444; }
        </style>
      </head>
      <body>
        <div class="title">TENTACIÓN FRUTAL</div>
        <div class="mesa-box">MESA ${datos.numero}${datos.nombre ? `<br>${escaparHtml(datos.nombre)}` : ""}</div>
        <img src="${datos.dataUrlQr}" alt="Código QR" />
        <div class="instrucciones">Escanea para ver el menú<br>y pedir desde tu celular</div>
        <div class="url">${escaparHtml(datos.url)}</div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 500);
          };
        </script>
      </body>
    </html>
  `;

  const ventana = window.open("", "_blank", "width=420,height=650");
  if (ventana) {
    ventana.document.write(html);
    ventana.document.close();
  }
}

/**
 * Dispara el pulso de apertura de cajón monedero para impresoras térmicas USB configuradas
 * con driver de Windows (que tienen habilitada la opción de "Abrir cajón al imprimir").
 * Imprime un documento mínimo invisible y se cierra instantáneamente.
 */
export function abrirCajonWindows() {
  const win = window.open("", "_blank", "width=150,height=150");
  if (!win) return;
  win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Abrir Cajón</title>
        <style>
          @page { size: 80mm 5mm; margin: 0; }
          body { margin: 0; padding: 0; background: transparent; }
        </style>
      </head>
      <body>
        <div style="font-size: 1px; color: transparent; line-height: 1;">.</div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() {
              window.close();
            }, 300);
          };
        </script>
      </body>
    </html>
  `);
  win.document.close();
}
